import { comonDataApi } from '@/api/config-setup'
import sitesApi from '@/api/operations/sitesApi'
import areaApi from '@/api/config/areaApi'
import emitterDataConfigApi from '@/api/config/emitterDataConfig'
import etReferenceDataConfig from '@/api/config/etReferenceDataConfig'
import hydraulicDataConfig from '@/api/config/hydraulicDataConfig'
import plantDataConfigApi from '@/api/config/plantDataConfigApi'
import soilDataConfigApi from '@/api/config/soilDataConfigApi'
import stationDataConfigApi from '@/api/config/stationDataConfig'
import InputFile from '@/components/form-control/InputFile'
import { CopyOneIcon, DatabaseAlerIcon } from '@/components/icons'
import { XLSX_ACEPT, XLSX_FORMAT } from '@/constant/common'
import { CONFIG_SETUP_ROUTES, OPERATIONS_ROUTES } from '@/constant/route'
import useCheckOverlay from '@/features/admin/hooks/locationInterface/useCheckOverlay'
import { useAuthContext } from '@/features/auth/context'
import SetUpHeaderModal from '@/features/design-system/components/common/SetUpHeaderModal'
import SetUpLayout from '@/features/design-system/components/common/SetUpLayout'
import MapViewRightMenu from '@/features/operations/components/pages/MapView/MapViewRightMenu'
import { NotifyService } from '@/helpers'
import { SearchStation } from '@/types'
import useSearchInterface from '@features/admin/hooks/interface/useSearchInterface'
import useSearchInterfaceId from '@features/admin/hooks/interface/useSearchInterfaceId'
import useValidateCurrentInterface from '@features/design-system/hooks/setup/useValidateCurrentInterface'
import { LoadingButton } from '@mui/lab'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import { saveAs } from 'file-saver'
import { useAppDispatch } from '@/store/stores'
import { updateInterfaceStatus } from '@/store/slices/interfaceByOrganization/interfaceByOrganizationSlice'
import { memo, useEffect, useState, type ReactNode } from 'react'
import toast from 'react-hot-toast'
import { useNavigate, useParams } from 'react-router-dom'
import { PinStationsOnMap } from '../SetUpThird/components'
import CommonDataTableV2 from '../SetUpSecond/CommonDataTableV2'
import { clearGuidedSetupProgress } from '../SetUpGuided/guided-setup-progress'
import classes from './SetUpSecondV2.styles.module.scss'
import { UploadProgressV2 } from './components/UploadProgressV2'
import { useCommonDataUploadV2 } from '@/features/design-system/hooks/setup/useCommonDataUploadV2'

const STEP_DISPLAY_NAMES: Record<string, string> = {
  'init-data-decoders': 'Initializing Data',
  'hydraulic': 'Hydraulic',
  'et-reference-and-plant': 'Plant & ET Reference',
  'soil': 'Soils',
  'emitter': 'Emitter',
  'area': 'Area & Zone',
  'sensor': 'Sensor',
  'et-zone': 'ET Zone',
  'station': 'Station',
  'finalize': 'Finalizing',
}

const formatUploadError = (error: string | null): string => {
  if (!error) return 'The file is not in the right format, or has typo errors'
  const match = error.match(/^Step '(.+?)' failed/)
  if (match) {
    const rawStep = match[1]
    const displayName = STEP_DISPLAY_NAMES[rawStep] || rawStep
    return `Step '${displayName}' failed. Could be missing data, please check '${displayName}' tab.`
  }
  return error
}

interface Props {}

enum STEP {
  CHOOSE_FILE = 0,
  UPLOADING,
  UPLOAD_ERROR,
  REVIEW,
  PIN_STATION,
}

// Helper to normalize API response to pagination format
// Some APIs return { data: [...], totalCount } while others return array directly
const toPaginationFormat = (response: any) => {
  if (!response) return null
  // Already has pagination format
  if (response.data && Array.isArray(response.data)) {
    return response
  }
  // Response is an array directly
  if (Array.isArray(response)) {
    return { data: response, totalCount: response.length }
  }
  // Other format - wrap it
  return { data: [], totalCount: 0 }
}

const BRAND = '#004744'
const BRAND_LIGHT = '#0A6B62'

// What the template actually carries — shown as chips so the page says what is
// being imported, not just how.
const IMPORT_DATASETS = [
  'Hydraulic',
  'ET Reference',
  'Plants',
  'Soils',
  'Emitters',
  'Areas & Zones',
  'Stations',
]

const stepActionSx = {
  height: 38,
  borderRadius: '10px',
  fontSize: '13px',
  fontWeight: 500,
  textTransform: 'none' as const,
  px: '18px',
}

const primaryActionSx = {
  ...stepActionSx,
  color: '#fff',
  background: `linear-gradient(120deg, ${BRAND} 0%, ${BRAND_LIGHT} 100%)`,
  boxShadow: '0 6px 16px rgba(0,71,68,0.22)',
  transition: 'box-shadow 200ms ease, transform 200ms ease',
  '&:hover': {
    background: `linear-gradient(120deg, ${BRAND} 0%, ${BRAND_LIGHT} 100%)`,
    boxShadow: '0 10px 22px rgba(0,71,68,0.30)',
    transform: 'translateY(-1px)',
  },
}

// 'todo' is available but not the step being pointed at — the template can be
// downloaded at any time, so it must not dim like a locked step.
type StepState = 'done' | 'active' | 'todo' | 'locked'

interface StepRowProps {
  index: number
  title: string
  description: string
  state: StepState
  last?: boolean
  action?: ReactNode
  children?: ReactNode
}

/**
 * SCRUM-1032 — one step of the Excel import, drawn as a real stepper.
 *
 * The marker sits outside the card and is joined to the next one by a rail, so
 * the three actions read as an ordered flow rather than three loose buttons.
 * The live step is the only one carrying brand weight; done steps recede and
 * upcoming ones dim.
 */
const StepRow = ({
  index,
  title,
  description,
  state,
  last,
  action,
  children,
}: StepRowProps) => {
  const isDone = state === 'done'
  const isActive = state === 'active'
  const isLocked = state === 'locked'

  return (
    <Stack direction="row" sx={{ gap: '16px', alignItems: 'stretch' }}>
      <Stack sx={{ alignItems: 'center', width: 34, flexShrink: 0 }}>
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '13px',
            fontWeight: 600,
            flexShrink: 0,
            color: isDone || isActive ? '#fff' : 'grey.600',
            background:
              isDone || isActive
                ? `linear-gradient(140deg, ${BRAND} 0%, ${BRAND_LIGHT} 100%)`
                : 'transparent',
            border: isDone || isActive ? 'none' : '1px solid',
            borderColor: 'border.main',
            boxShadow: isActive ? '0 6px 16px rgba(0,71,68,0.25)' : 'none',
          }}
        >
          {isDone ? <CheckRoundedIcon sx={{ fontSize: 18 }} /> : index}
        </Box>
        {!last && (
          <Box
            sx={{
              flex: 1,
              width: '2px',
              my: '8px',
              borderRadius: '1px',
              backgroundColor: isDone ? 'primary.main' : 'border.main',
              opacity: isDone ? 0.35 : 1,
            }}
          />
        )}
      </Stack>

      <Stack
        sx={{
          flex: 1,
          minWidth: 0,
          mb: last ? 0 : '4px',
          p: '20px 22px',
          borderRadius: '18px',
          border: '1px solid',
          borderColor: isActive ? 'transparent' : 'border.main',
          backgroundColor: 'background.default',
          opacity: isLocked ? 0.65 : 1,
          position: 'relative',
          overflow: 'hidden',
          transition: 'transform 200ms ease, box-shadow 200ms ease',
          boxShadow: (theme) =>
            theme.palette.mode === 'dark'
              ? 'none'
              : isActive
                ? '0 14px 32px rgba(0,71,68,0.12)'
                : '0 1px 2px rgba(17,49,46,0.04)',
          ...(isActive && {
            outline: '1px solid',
            outlineColor: 'rgba(0,71,68,0.35)',
            outlineOffset: '-1px',
            // Brand light bleeding in behind the live step.
            '&::after': {
              content: '""',
              position: 'absolute',
              top: -120,
              right: -80,
              width: 260,
              height: 260,
              borderRadius: '50%',
              background: `radial-gradient(circle, ${BRAND}14 0%, transparent 70%)`,
              pointerEvents: 'none',
            },
          }),
          '&:hover': isLocked
            ? undefined
            : {
                transform: 'translateY(-2px)',
                boxShadow: (theme) =>
                  theme.palette.mode === 'dark'
                    ? 'none'
                    : '0 16px 34px rgba(0,71,68,0.12)',
              },
        }}
      >
        <Stack
          direction="row"
          sx={{
            alignItems: 'center',
            gap: '16px',
            flexWrap: 'wrap',
            zIndex: 1,
          }}
        >
          <Stack sx={{ flex: 1, minWidth: 220, gap: '3px' }}>
            <Typography
              sx={{ color: 'text.main', fontSize: '15px', fontWeight: 600 }}
            >
              {title}
            </Typography>
            <Typography
              sx={{ color: 'grey.600', fontSize: '13px', lineHeight: 1.5 }}
            >
              {description}
            </Typography>
          </Stack>
          {action}
        </Stack>
        {children}
      </Stack>
    </Stack>
  )
}

export const SetUpSecondV2 = memo((props: Props) => {
  // SCRUM-976: the URL :id is the single source of truth for the Common Data setup page.
  // The globally-selected interface (AuthContext, backed by localStorage) must NOT drive any
  // interface-scoped action here - another tab can change it via the cross-tab storage sync,
  // or it can be stale after a refresh/bookmark. That previously made the upload target a
  // different interface than the one the page (and the reset step, which already uses :id) was
  // showing. We keep a read of the global value only to surface a mismatch warning below.
  const { interfaceId: globalInterfaceId } = useAuthContext()
  const { id } = useParams()
  const interfaceId = Number(id)
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  // Step state
  const [step, setStep] = useState(STEP.CHOOSE_FILE)

  // Reset data state
  const [isDeleted, setIsDeleted] = useState(false)
  const [loadingReset, isLoadingReset] = useState(false)
  const [isSetup, setIsSetup] = useState(false)

  // Download template state
  const [isDownloading, setIsDownloading] = useState(false)

  // Cancel confirmation dialog state
  const [showCancelConfirm, setShowCancelConfirm] = useState(false)

  // Review data state - fetched from multiple APIs after import completes
  const [reviewData, setReviewData] = useState<{
    hydraulic: any
    etReference: any
    plant: any
    emitter: any
    areaZone: any
    soil: any
    station: any
  } | null>(null)
  const [isLoadingReview, setIsLoadingReview] = useState(false)
  const [param] = useState<SearchStation>({
    PageSize: 0,
    interfaceId: Number(interfaceId),
  })

  // Interface validation
  const { data: allInterfaces } = useSearchInterface({ PageSize: 0 })
  const { refetch: refetchInterfaceById } = useSearchInterfaceId(id)
  const { selectedInterface } = useValidateCurrentInterface({
    currentInterfaceId: id,
    allInterfaces,
    redirectTo: OPERATIONS_ROUTES.index.to,
    autoRedirect: false, // We handle redirect manually below
  })

  // Redirect if interface is already setup (status === true)
  // BUT only when first entering the page (CHOOSE_FILE step)
  // Don't redirect when user is actively in UPLOADING/REVIEW flow
  useEffect(() => {
    if (
      selectedInterface &&
      selectedInterface.status === true &&
      step === STEP.CHOOSE_FILE
    ) {
      navigate(OPERATIONS_ROUTES.index.to)
    }
  }, [selectedInterface, step, navigate])

  // Upload hook V2
  const uploadHook = useCommonDataUploadV2({
    interfaceId: Number(interfaceId),
    onSuccess: () => {
      setStep(STEP.REVIEW)
    },
    onError: (error) => {
      setStep(STEP.UPLOAD_ERROR)
      NotifyService.error(error)
    },
    onCancel: async () => {
      // After cancel completes and no active task, check interface status
      const { data: interfaceData } = await refetchInterfaceById()

      if (interfaceData?.status === true) {
        // Sync Redux store before navigating to prevent redirect loop
        dispatch(updateInterfaceStatus({ id: Number(id), status: true }))
        navigate(OPERATIONS_ROUTES.index.to)
      } else {
        // Interface not setup, require Reset Data before upload
        setIsDeleted(false)
        setStep(STEP.CHOOSE_FILE)
      }
      toast.success('Import cancelled')
    },
  })

  // Cancel handlers
  const handleCancelClick = () => {
    setShowCancelConfirm(true)
  }

  const handleConfirmCancel = async () => {
    setShowCancelConfirm(false)
    await uploadHook.cancelTask()
  }

  const handleCloseCancelConfirm = () => {
    setShowCancelConfirm(false)
  }

  // Check for active task on mount
  useEffect(() => {
    const checkForActiveTask = async () => {
      const activeTask = await uploadHook.checkActiveTask()
      if (activeTask?.hasActiveTask) {
        setStep(STEP.UPLOADING)
      }
    }

    checkForActiveTask()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Fetch all review data from multiple APIs when entering review step
  useEffect(() => {
    const fetchAllReviewData = async () => {
      setIsLoadingReview(true)
      try {
        const searchParams = { interfaceId: Number(interfaceId), PageSize: 0 }

        // Fetch all data in parallel
        const [
          hydraulicRes,
          etReferenceRes,
          plantRes,
          emitterRes,
          areaRes,
          soilRes,
          stationRes,
        ] = await Promise.all([
          hydraulicDataConfig.getAll(searchParams).catch(() => ({ data: null })),
          etReferenceDataConfig.getAll(searchParams).catch(() => ({ data: null })),
          plantDataConfigApi.getAll(searchParams).catch(() => ({ data: null })),
          emitterDataConfigApi.getAll(searchParams).catch(() => ({ data: null })),
          areaApi.getAll(searchParams).catch(() => ({ data: null })),
          soilDataConfigApi.getAll(searchParams).catch(() => ({ data: null })),
          stationDataConfigApi.getListWithDetail(Number(interfaceId), param).catch(() => ({ data: null })),
        ])

        setReviewData({
          hydraulic: toPaginationFormat(hydraulicRes?.data),
          etReference: toPaginationFormat(etReferenceRes?.data),
          plant: toPaginationFormat(plantRes?.data),
          emitter: toPaginationFormat(emitterRes?.data),
          areaZone: areaRes?.data, // Area has special format { data: [...], totalCount }
          soil: toPaginationFormat(soilRes?.data),
          station: stationRes?.data,
        })
      } catch (error) {
        console.error('Failed to fetch review data:', error)
      } finally {
        setIsLoadingReview(false)
      }
    }

    if (step === STEP.REVIEW) {
      fetchAllReviewData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, interfaceId])

  // Navigate when upload completes
  useEffect(() => {
    if (uploadHook.isCompleted) {
      setStep(STEP.REVIEW)
    }
  }, [uploadHook.isCompleted])

  // Handle error state
  useEffect(() => {
    if (uploadHook.isFailed) {
      setStep(STEP.UPLOAD_ERROR)
    }
  }, [uploadHook.isFailed])

  // SCRUM-976 safety net: surface (do not block) when the globally-selected interface differs
  // from the one being set up. The page already targets the URL :id for every action, so the
  // upload is safe regardless - this only flags the divergence for observability (Seq/console)
  // and warns the user. We intentionally do NOT hard-block: a mismatch is legitimate when the
  // page is reached via refresh/bookmark, and blocking would prevent a valid setup. We also do
  // NOT auto-sync the global value here, to avoid disrupting another tab via the storage event.
  useEffect(() => {
    if (
      globalInterfaceId != null &&
      !Number.isNaN(interfaceId) &&
      Number(globalInterfaceId) !== interfaceId
    ) {
      console.warn(
        `[SetUpSecondV2] Selected-interface mismatch (SCRUM-976): global=${globalInterfaceId} url=${interfaceId}. Using the URL interface for setup/upload.`
      )
      toast.error(
        'Heads up: your selected controller differs from the one open here. This page will set up the controller shown in the address bar.'
      )
    }
  }, [globalInterfaceId, interfaceId])

  const { data: errorOverlay } = useCheckOverlay(interfaceId)
  const [hasSites, setHasSites] = useState(true)

  useEffect(() => {
    if (!interfaceId) return
    sitesApi
      .getSites(Number(interfaceId))
      .then((res) => setHasSites(res.data.data?.length > 0))
      .catch(() => setHasSites(false))
  }, [interfaceId])

  const onBack = () => {
    navigate(CONFIG_SETUP_ROUTES.setupFirst.to(id || ''))
  }

  const onErrors = () => {}

  const onDownload = async () => {
    setIsDownloading(true)
    try {
      const res = await comonDataApi.getCommonTemplate(Number(interfaceId))
      saveAs(res.data, 'CommonData_Template.xlsx')
    } catch (error: any) {
      if (error?.statusCode === 404) {
        toast.error('Controller does not exist')
      } else {
        toast.error('Failed to download the template. Please try again later.')
      }
    } finally {
      setIsDownloading(false)
    }
  }

  const onClose = () => {
    navigate(OPERATIONS_ROUTES.index.to)
  }

  const onTryAgain = () => {
    setIsDeleted(false)
    setStep(STEP.CHOOSE_FILE)
  }

  /**
   * @description:
   * check carefully before delete data
   */
  const handleDelete = async () => {
    isLoadingReset(true)
    try {
      // Step 1: Send the initial request with type = 1 and page = 1
      let type = 1
      let page = 1

      let res = await comonDataApi.deleteCommonType(Number(id), type)

      if (res.data.success === true) {
        type = 2
        do {
          res = await comonDataApi.deleteCommonType(Number(id), type, page)
          page += 1
        } while (res.data.isPaging === true)

        // Step 3: Send the final request with type = 3
        type = 3
        res = await comonDataApi.deleteCommonType(Number(id), type)

        if (res.data.success === true) {
          clearGuidedSetupProgress(Number(id))
          toast.success('Reset data successful')
          setIsDeleted(true)
          isLoadingReset(false)
        } else {
          toast.error(res.data.message)
          setIsDeleted(false)
        }
      } else {
        isLoadingReset(false)
        toast.error(res.data.message)
      }
    } catch (error) {
      isLoadingReset(false)
      toast.error('Delete failed. Please check your connection.')
    } finally {
      isLoadingReset(false)
      setIsSetup(false)
    }
  }

  const onChangeFile = async (file: File[]) => {
    if (!file?.length) return

    setStep(STEP.UPLOADING)
    await uploadHook.upload(file[0])
  }

  const handleNext = async () => {
    if (!!errorOverlay) {
      setStep(STEP.PIN_STATION)
    } else {
      // Poll interface status until true or timeout (max 60 seconds)
      const MAX_WAIT_TIME = 60000
      const POLL_INTERVAL = 2000
      const startTime = Date.now()

      while (Date.now() - startTime < MAX_WAIT_TIME) {
        const { data: interfaceData } = await refetchInterfaceById()

        if (interfaceData?.status === true) {
          // Sync Redux store before navigating to prevent infinite redirect loop
          // SelectLabelDashboard reads status from Redux (stale) while SetUpContainer
          // reads from React Query (fresh) - mismatch causes redirect loop
          dispatch(updateInterfaceStatus({ id: Number(id), status: true }))
          navigate(OPERATIONS_ROUTES.index.to)
          return
        }

        // Wait before next poll
        await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL))
      }

      // Timeout reached
      toast.error('Interface setup timed out. Please try again.')
    }
  }

  const render = () => {
    switch (step) {
      case STEP.CHOOSE_FILE:
        return (
          <Stack sx={{ flex: 1, alignItems: 'center', p: '28px 24px 56px' }}>
            <Stack sx={{ width: '100%', maxWidth: 760, gap: '24px' }}>
              {/* Hero — what this page imports, before how. */}
              <Box
                sx={{
                  position: 'relative',
                  overflow: 'hidden',
                  borderRadius: '22px',
                  p: '30px 32px',
                  border: '1px solid',
                  borderColor: 'border.main',
                  background: (theme) =>
                    theme.palette.mode === 'dark'
                      ? 'linear-gradient(130deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.015) 65%)'
                      : `linear-gradient(130deg, ${BRAND}0F 0%, ${BRAND_LIGHT}05 60%, transparent 100%)`,
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: -140,
                    right: -90,
                    width: 320,
                    height: 320,
                    borderRadius: '50%',
                    background: `radial-gradient(circle, ${BRAND}1A 0%, transparent 70%)`,
                    pointerEvents: 'none',
                  },
                }}
              >
                <Stack sx={{ gap: '14px', position: 'relative', zIndex: 1 }}>
                  <Stack
                    direction="row"
                    sx={{ alignItems: 'center', gap: '14px' }}
                  >
                    <Box
                      sx={{
                        width: 46,
                        height: 46,
                        borderRadius: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#fff',
                        background: `linear-gradient(140deg, ${BRAND} 0%, ${BRAND_LIGHT} 100%)`,
                        boxShadow: '0 10px 22px rgba(0,71,68,0.28)',
                        '& svg': { width: 22, height: 22 },
                      }}
                    >
                      <CopyOneIcon />
                    </Box>
                    <Stack sx={{ gap: '2px' }}>
                      <Typography
                        sx={{
                          color: 'text.main',
                          fontSize: '24px',
                          fontWeight: 600,
                          letterSpacing: '-0.01em',
                        }}
                      >
                        Import common data
                      </Typography>
                      <Typography
                        sx={{ color: 'grey.600', fontSize: '13px' }}
                      >
                        Fill one spreadsheet, upload it once — every dataset
                        lands together.
                      </Typography>
                    </Stack>
                  </Stack>

                  <Stack
                    direction="row"
                    sx={{ gap: '8px', flexWrap: 'wrap' }}
                  >
                    {IMPORT_DATASETS.map((dataset) => (
                      <Box
                        key={dataset}
                        sx={{
                          height: 24,
                          px: '10px',
                          display: 'flex',
                          alignItems: 'center',
                          borderRadius: '12px',
                          fontSize: '11px',
                          fontWeight: 500,
                          color: 'primary.main',
                          backgroundColor: (theme) =>
                            theme.palette.mode === 'dark'
                              ? 'rgba(255,255,255,0.08)'
                              : 'rgba(0,71,68,0.07)',
                        }}
                      >
                        {dataset}
                      </Box>
                    ))}
                  </Stack>
                </Stack>
              </Box>

              <Stack>
                <StepRow
                  index={1}
                  state={isDeleted ? 'done' : 'active'}
                  title="Reset common data"
                  description="Clears the controller's existing common data so the import starts from a clean slate."
                  action={
                    <LoadingButton
                      variant={isDeleted ? 'secondary' : 'primary'}
                      sx={isDeleted ? stepActionSx : primaryActionSx}
                      onClick={() => {
                        if (!selectedInterface?.status) {
                          setIsSetup(true)
                        } else {
                          handleDelete()
                        }
                      }}
                      startIcon={<CopyOneIcon />}
                      loading={loadingReset}
                    >
                      {isDeleted ? 'Reset again' : 'Reset data'}
                    </LoadingButton>
                  }
                />

                <StepRow
                  index={2}
                  state="todo"
                  title="Fill in the Excel template"
                  description="Download the template and enter your pipes & pumps, plants, soils, areas and stations."
                  action={
                    <LoadingButton
                      variant="secondary"
                      sx={stepActionSx}
                      onClick={onDownload}
                      startIcon={<CopyOneIcon />}
                      loading={isDownloading}
                    >
                      Download template
                    </LoadingButton>
                  }
                />

                <StepRow
                  index={3}
                  last
                  state={isDeleted ? 'active' : 'locked'}
                  title="Upload the XLSX"
                  description={
                    isDeleted
                      ? 'Drop the completed template here — every dataset is imported in one pass.'
                      : 'Unlocks once the reset in step 1 has run.'
                  }
                >
                  <Box sx={{ mt: '16px', opacity: isDeleted ? 1 : 0.5 }}>
                    <InputFile
                      accept={XLSX_ACEPT}
                      formats={XLSX_FORMAT}
                      disabled={!isDeleted}
                      onChange={onChangeFile}
                      BoxProps={{
                        // `&&` outranks InputFile's own module class, which
                        // pins the dropzone to a fixed 439px.
                        sx: {
                          '&&': { width: '100%', height: 190 },
                          borderRadius: '14px',
                          border: '1.5px dashed',
                          borderColor: 'border.main',
                          background: (theme: any) =>
                            theme.palette.mode === 'dark'
                              ? 'rgba(255,255,255,0.03)'
                              : `linear-gradient(160deg, ${BRAND}08 0%, transparent 70%)`,
                          transition:
                            'border-color 180ms ease, background-color 180ms ease',
                          ...(isDeleted && {
                            '&:hover': {
                              borderColor: 'primary.main',
                              backgroundColor: 'rgba(0,71,68,0.05)',
                            },
                          }),
                          // The icon carries the brand weight here.
                          '& svg': {
                            marginTop: '38px !important',
                            width: 40,
                            height: 40,
                          },
                        },
                      }}
                    />
                  </Box>
                </StepRow>
              </Stack>
            </Stack>
          </Stack>
        )

      case STEP.UPLOADING:
        return (
          <Box className={classes.root}>
            <Typography className={classes.pageTitle}>Common Data</Typography>
            <UploadProgressV2
              progress={uploadHook.overallProgress}
              currentStep={uploadHook.currentStepName}
              totalSteps={uploadHook.progress?.steps.length || 0}
              completedSteps={
                uploadHook.progress?.steps.filter((s) => s.status === 2).length || 0
              }
              isUploading={uploadHook.isUploading}
              isCancelling={uploadHook.isCancelling}
              onCancel={handleCancelClick}
            />
            <Typography className={classes.title}>Processing Data</Typography>
            <Typography className={classes.description}>
              Please do not close the window
            </Typography>
          </Box>
        )

      case STEP.UPLOAD_ERROR:
        return (
          <Box className={classes.root}>
            <Typography className={classes.pageTitle}>Common Data</Typography>
            <DatabaseAlerIcon className={classes.errorIcon} />
            <Typography className={classes.title}>
              Unable to import data
            </Typography>
            <Typography className={classes.description}>
              {formatUploadError(uploadHook.error)}
            </Typography>
            <Box className={classes.actions}>
              <Button className={classes.btnTry} onClick={onTryAgain}>
                Try Again
              </Button>
            </Box>
          </Box>
        )

      case STEP.REVIEW:
        return (
          <>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              className={classes.Table}
            >
              <Typography className={classes.TableTitle}>
                Review Common Data
              </Typography>
              <Stack alignItems="center">
                <Button
                  variant="contained"
                  className={classes.btnTry}
                  onClick={() => handleNext()}
                  disabled={isLoadingReview}
                >
                  {isLoadingReview ? 'Loading...' : 'Next'}
                </Button>
              </Stack>
            </Stack>
            <CommonDataTableV2
              dataHydraulic={reviewData?.hydraulic}
              dataEt={reviewData?.etReference}
              dataPlant={reviewData?.plant}
              dataEmitter={reviewData?.emitter}
              dataAreas={reviewData?.areaZone?.data}
              dataSoil={reviewData?.soil}
              totalCountAreas={reviewData?.areaZone?.totalCount}
            />
          </>
        )

      case STEP.PIN_STATION:
        return (
          <PinStationsOnMap
            interfaceId={Number(interfaceId)}
            data={reviewData?.station}
            onClose={onClose}
          />
        )
    }
  }

  return (
    <SetUpLayout
      title="Common Data"
      rightLayout={
        step === STEP.PIN_STATION || !errorOverlay || !hasSites ? null : (
          <Stack className={classes.mapViewRight}>
            <MapViewRightMenu />
          </Stack>
        )
      }
      header={
        step === STEP.PIN_STATION ? null : (
          <SetUpHeaderModal
            title="Common Data"
            onCancel={step === STEP.UPLOADING ? onErrors : onBack}
            showBtnClose={!(step === STEP.REVIEW)}
          />
        )
      }
      fullWidth={step === STEP.PIN_STATION ? true : false}
    >
      {render()}

      <Dialog
        open={isSetup}
        onClose={() => {
          setIsSetup(false)
        }}
      >
        <Box
          sx={{
            backgroundColor: 'background.default',
          }}
        >
          <Stack sx={{ padding: '14px 30px' }}>
            <Typography
              sx={{
                color: 'text.main',
                fontWeight: 400,
                fontSize: '20px',
                lineHeight: '24px',
                minHeight: '24px',
                minWidth: '192px',
              }}
            >
              Warning
            </Typography>
          </Stack>

          <Stack
            sx={{
              backgroundColor: 'common.white',
              minWidth: '378px',
              padding: '30px',
              boxShadow: '0px 0px 30px rgba(132, 132, 132, 0.25)',
            }}
          >
            <Stack>
              <Typography
                sx={{
                  color: '#000000',
                  marginBottom: '10px',
                }}
                variant="h3"
              >
                You're resetting all data for the controller{' '}
                <b>
                  {selectedInterface?.interfaceCustomName} (
                  {selectedInterface?.interface_name})
                </b>
              </Typography>

              <Typography
                sx={{
                  fontSize: '14px',
                  color: '#FF0000',
                  marginBottom: '20px',
                }}
                variant="h3"
              >
                This action is not reversible, would you like to proceed?
              </Typography>
            </Stack>

            <Box
              sx={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}
            >
              <Button
                variant="secondary"
                className={classes.btnTry}
                onClick={() => setIsSetup(false)}
              >
                Close
              </Button>

              {loadingReset ? (
                <LoadingButton
                  className={classes.btnTry}
                  startIcon={<CopyOneIcon />}
                  loading
                  variant="contained"
                >
                  Confirm
                </LoadingButton>
              ) : (
                <Button
                  variant="contained"
                  className={classes.btnTry}
                  onClick={() => handleDelete()}
                >
                  Confirm
                </Button>
              )}
            </Box>
          </Stack>
        </Box>
      </Dialog>

      {/* Cancel import confirmation dialog */}
      <Dialog open={showCancelConfirm} onClose={handleCloseCancelConfirm}>
        <Box sx={{ backgroundColor: 'background.default' }}>
          <DialogTitle sx={{ padding: '41px 152px 4px 34px' }}>
            <Typography
              sx={{
                color: 'text.main',
                fontWeight: 400,
                fontSize: '20px',
                lineHeight: '24px',
                minHeight: '24px',
                minWidth: '192px',
              }}
            >
              Cancel Import?
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ paddingLeft: '34px', paddingBottom: '18px' }}>
            <Typography
              sx={{
                color: 'text.main',
                fontWeight: 400,
                fontSize: '12px',
                lineHeight: '1.3',
                maxWidth: '314px',
              }}
            >
              Are you sure you want to cancel the import? This will stop the
              current import process.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ paddingBottom: '32px', paddingRight: '30px' }}>
            <Button
              variant="secondary"
              sx={{ width: '99px' }}
              onClick={handleCloseCancelConfirm}
            >
              No
            </Button>
            <Button
              variant="primary"
              sx={{ width: '99px' }}
              onClick={handleConfirmCancel}
            >
              Yes
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </SetUpLayout>
  )
})

SetUpSecondV2.displayName = 'SetUpSecondV2'

export default SetUpSecondV2
